/**
 * The product implementation of `V07Commands`: the same acts, against the real backend.
 *
 * Three rules hold everywhere in this file.
 *
 * It never decides authorization. Every call goes to the route the authorization matrix governs,
 * and a 403 comes back as the backend's own refusal for the screen to show. There is no
 * client-side role check that could disagree with the server's.
 *
 * It never invents an outcome. A command returns `ok: false` with a legible message whenever the
 * backend refused, and the refusal text is the API error's message — never a constraint name, a
 * SQLSTATE, a stack trace or a raw driver error, none of which the backend emits (PO 7.8 §5).
 *
 * It never pretends to have persisted something. Where the product has no route — the routeless
 * write commands, and the settings whose storage is an open Product Owner question — the command
 * answers `ok: false` and says so, so the screen can tell the truth instead of showing a toast
 * over a world that forgot.
 *
 * After every act that changed the world it re-reads it: `refresh()` reloads the projection the
 * screens render, so what the user sees next is what the backend actually holds rather than what
 * the click hoped for.
 */
import type { ApiClient } from '../../api/client';
import { newCorrelationId, newIdempotencyKey } from '../../api/client';
import { type ApiError, fieldIssueSentence, isApiError, userMessage } from '../../api/errors';
import {
  activateUser,
  adminPublishCredential,
  archiveTemplate,
  assignRole,
  cloneTemplate,
  correctCredential,
  correctRecipientEmail,
  createIssuerProfile,
  createLinkedInSync,
  createTemplate,
  createUser,
  deleteDraft,
  disableUser,
  exportCredentialsCsv,
  issueCredential,
  listUsers,
  publishRelease,
  addCredentialsToGroup,
  createCredentialGroup,
  deleteCredentialGroup,
  removeCredentialsFromGroup,
  renameIssuanceBatch,
  renameTemplate,
  updateCredentialGroup,
  readAssetContent,
  readCredentialByCode,
  exportCredentialHistoryCsv,
  readCredentialHistory,
  readFontContent,
  readSheetSync,
  reissueCredential,
  listFirstSendRequests,
  createBulkClaimLinks as createBulkClaimLinksApi,
  previewBulkClaimLinks as previewBulkClaimLinksApi,
  previewFirstSend,
  readFirstSendRequest,
  requestFirstSend,
  resendClaimEmail,
  revokeCredential,
  runSheetSync,
  saveSheetSync,
  sendEmailTemplateTest,
  updateDraft,
  updateEmailTemplate,
  updateIssuerProfile,
  uploadAsset as uploadAssetBytes,
  uploadFont as uploadFontBytes,
} from '../../api/internal';
import {
  acceptClaim,
  declineClaim,
  recordPublicEngagement,
  recordPublicInteraction,
} from '../../api/publicApi';
import { libraryAssetFromApi, libraryFontFromApi, loadFontFace } from '../lib/asset-library';
import { holderKeyOfRelease } from '../lib/credential-register';
import { registerRenderLibrary } from '../lib/render/library';
import type {
  AdministrativeUserView,
  CommandResult,
  CorrectCommandInput,
  IssueCommandInput,
  V07Commands,
} from './commands';
import { attemptKeyForFile, followBatch, forgetBatchId, openBatch, startServerRun } from './batch-run';
import { credentialFromListRow, hydrateCredential as hydrateCredentialFromApiReads } from './from-api';
import { draftToApi } from './to-api';
import type { DemoCredential, DemoDatabase, DemoRelease } from './types';

/**
 * The prototype's column mapping, in the vocabulary the backend validates.
 *
 * The screen maps each spreadsheet column to a FIELD KEY, because that is what the reference's own
 * mapping table is a list of. The backend's targets are a different vocabulary: a claim is
 * `claim:<key>`, and five issuance inputs that are not claims have their own names
 * (`recipientEmail`, `issueDate`, `expiresAt`, `externalReference`, `lang` — `domain/batch/mapping.ts`).
 *
 * Sent untranslated, every target came back `mapping_unknown_target` and the whole batch was
 * refused — including the holder field, which then also reported «sahip alanı eşlenmemiş». The
 * screen had suggested a mapping that was correct in its own terms and impossible in the server's.
 */
const BATCH_INPUT_TARGETS: Record<string, string> = {
  email: 'recipientEmail',
  recipientEmail: 'recipientEmail',
  recipient_email: 'recipientEmail',
  issue_date: 'issueDate',
  issueDate: 'issueDate',
  expiry_date: 'expiresAt',
  expiresAt: 'expiresAt',
  external_reference: 'externalReference',
  externalReference: 'externalReference',
  lang: 'lang',
};

export function batchMappingToApi(mapping: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [header, target] of Object.entries(mapping)) {
    if (!target) continue;
    /* Already in the server's vocabulary — the screen may hand back what a previous run stored. */
    if (target.startsWith('claim:')) {
      out[header] = target;
      continue;
    }
    out[header] = BATCH_INPUT_TARGETS[target] ?? `claim:${target}`;
  }
  return out;
}

export interface ApiCommandDeps {
  api: ApiClient;
  /** The projection the screens render. Re-read after every act that changed the world. */
  refresh: () => Promise<void>;
  /** The current projection, for the id lookups the screens address records by. */
  read: () => DemoDatabase;
  /** Local-only screen state (the publish gate's acknowledgement and its kind). */
  patchLocal: (mutate: (db: DemoDatabase) => void) => void;
}

const ok = (over: Partial<CommandResult> = {}): CommandResult => ({ ok: true, ...over });
const no = (message: string): CommandResult => ({ ok: false, message });

/**
 * One place every refusal passes through.
 *
 * An `ApiError` already carries the backend's own message and correlation id; anything else is a
 * transport failure, which the user is told about as a transport failure rather than as a refusal
 * the backend never issued.
 */
/**
 * What a refusal SAYS, as opposed to what the backend logged.
 *
 * The backend's own `message` is written for the answer, not for the operator: a schema refusal
 * reads «request shape is invalid», which is English, is true of the whole request, and names
 * nothing. The answer already carries what was wrong — `error.details` says `subject.displayName`
 * — so when there are details the sentence is the product's own, and it names the field. Without
 * details the backend's message is kept, because those are written to be shown.
 */
function refusalMessage(e: ApiError): string {
  if (e.kind === 'network') return userMessage(e);
  const fields = fieldIssueSentence(e);
  return fields === null ? e.message : `${userMessage(e)} ${fields}`;
}

function failure(e: unknown): CommandResult {
  if (isApiError(e))
    return {
      ok: false,
      /*
       * A request that never reached the backend carries the BROWSER's words — «Failed to fetch» —
       * and those are not a refusal the backend issued, which is what this slot is for. The product
       * already has its own sentence for every transport code (`userMessage`); a real refusal keeps
       * the backend's own message, which is written to be shown.
       */
      message: refusalMessage(e),
      /*
       * Did this request happen, or not?
       *
       * «The backend answered ⇒ it decided» is only true of the answers the backend gives ON
       * PURPOSE. A 5xx is not one of them, and issuance is the proof: the credential is committed
       * in its own transaction and the claim e-mail is enqueued AFTER it
       * (`use-cases/issuance.ts` — the enqueue sits outside `tm.run`, and its `catch` releases the
       * idempotency record and rethrows). A queue that is down therefore answers 500 about a
       * document that exists. Reporting that as «refused» told the operator nothing was created and
       * threw away the intent that could have proved otherwise.
       *
       * So: the browser never reached the server, or the server fell over ⇒ nobody knows. Every
       * deliberate refusal — 4xx, with the backend's own code — is a decision.
       */
      outcome: e.kind === 'network' || e.status >= 500 ? 'unresolved' : 'refused',
      /* The backend's own code, so a screen can tell two refusals apart without parsing prose. */
      ...(e.code ? { code: e.code } : {}),
      /* The rejected fields themselves, for a screen that wants to mark its own inputs. */
      ...(e.details.length > 0 ? { details: e.details.map((d) => ({ ...d })) } : {}),
      ...(e.correlationId ? { correlationId: e.correlationId } : {}),
    };
  return { ...no('Sunucuya ulaşılamadı'), outcome: 'unresolved' };
}

/**
 * The backend's closed correction reason code, derived from what actually changed.
 *
 * `CORRECTION_REASON_CODES` is a fixed set (`issue_date_error`, `expiry_date_error`,
 * `certification_period_error`, `claim_value_error`, `render_text_error`, `other`) and anything
 * else is refused with `correction_reason_unknown`. The reference's own dialog asks for a free-text
 * reason and has no code field, so the code is derived here rather than invented as a control the
 * prototype does not have — and `other` is the honest default when the change is mixed.
 */
function correctionReasonCode(input: CorrectCommandInput): string {
  /*
   * `other` is deliberately NOT returned: the backend requires an internal note for it, and the
   * reference's dialog has no field for one. A mixed change is reported as a claim correction,
   * which is what it is — the free-text reason the user typed carries the rest.
   */
  if (Object.keys(input.claims).length > 0) return 'claim_value_error';
  return 'expiry_date_error';
}

/** The holder field the release names — resolved against the fields it actually carries. */
const holderKeyOf = (release: DemoRelease): string => holderKeyOfRelease(release);

function issueBody(input: IssueCommandInput): {
  templateReleaseId: string;
  subject: { displayName: string; contactEmail: string | null };
  claims: Record<string, unknown>;
  issueDate: string;
  expiresAt: string | null;
  lang: string;
  sendEmail: boolean;
  recipientEmail: string | null;
} {
  const email = input.email.trim();
  return {
    templateReleaseId: input.release.id,
    /*
     * Rule 5: identity is never merged on a name, so no `subjectId` is sent and the backend opens
     * its own subject record. The contact address is private data, carried beside the claims and
     * never into them.
     */
    subject: {
      displayName: (input.claims[holderKeyOf(input.release)] ?? '').trim(),
      contactEmail: email || null,
    },
    claims: { ...input.claims },
    issueDate: input.issueDate,
    expiresAt: input.expiresAt,
    lang: input.lang,
    sendEmail: input.sendEmail,
    recipientEmail: email || null,
  };
}

export function createApiCommands(deps: ApiCommandDeps): V07Commands {
  /*
   * One correlation id per command, stamped on every request the command makes.
   *
   * A click can be more than one request — correcting a recipient's address and resending the link
   * is two — and this is what ties them to each other and to the audit rows the backend wrote. The
   * id comes back in the result, so a screen can show it and a test can follow the click into the
   * ledger. It is `corr_` + 12 hex and carries no user data.
   */
  const correlated = (): { api: ApiClient; cid: string } => {
    const cid = newCorrelationId();
    return { api: deps.api.withCorrelation(cid), cid };
  };

  /**
   * The screens address records by public code; every route addresses them by id.
   *
   * The store is where the answer usually is — whatever the reader has on screen is in it — but it is
   * no longer the whole register, so «not in the store» no longer means «does not exist». When it is
   * absent the id is resolved through the authorized register search, the same `credential.read` cell
   * and the same filter the toolbar uses. Without this, a revoke opened from page nine of the
   * register, or a detail screen reached straight from a link, reported «Kayıt bulunamadı» about a
   * record the session was perfectly entitled to act on.
   */
  const idOf = async (code: string): Promise<DemoCredential | undefined> => {
    const local = deps.read().credentials.find((c) => c.publicCode === code);
    if (local) return local;
    try {
      /* The SAME resolution the detail screen uses (`/credentials/by-code/:code`), so a command and
         the screen that offered it can never disagree about which record the code names. */
      const { credential: row } = await readCredentialByCode(deps.api, code);
      const releases = new Map<string, DemoRelease>();
      for (const t of deps.read().templates) for (const r of t.releases) releases.set(r.id, r);
      return credentialFromListRow(row, releases.get(row.templateReleaseId) ?? null);
    } catch (e) {
      /* A code that resolves to nothing is «not found» for the caller; anything else is the
         backend's refusal or failure and must reach the screen as itself. */
      if (isApiError(e) && e.status === 404) return undefined;
      throw e;
    }
  };

  const after = async <T>(result: T, over: Partial<CommandResult> = {}): Promise<CommandResult> => {
    await deps.refresh();
    void result;
    return ok(over);
  };

  return {
    // ------------------------------------------------------------------ credentials
    async issueCredential(input) {
      const { api, cid } = correlated();
      try {
        const r = await issueCredential(api, issueBody(input), input.idempotencyKey ?? newIdempotencyKey());
        return await after(r, { publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async reissueCredential(input) {
      const { api, cid } = correlated();
      const old = await idOf(input.supersedesCode);
      if (!old) return no('Kayıt bulunamadı');
      try {
        /*
         * Rule 8: revoke and reissue are one transaction, and it is the backend's transaction. The
         * screen never revokes first and issues second — a failure between the two would leave a
         * recipient with neither record.
         */
        const r = await reissueCredential(api, old.id, {
          reasonCode: 'correction',
          reason: input.reason,
          newIssue: issueBody(input),
        });
        return await after(r, { publicCode: r.created.publicCode, correlationId: r.correlationId || cid });
      } catch (e) {
        return failure(e);
      }
    },

    async revokeCredential(input) {
      const { api, cid } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      try {
        const r = await revokeCredential(api, c.id, {
          reasonCode: 'issued_in_error',
          reason: input.reason,
        });
        return await after(r, { publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /**
     * The register as a file, composed by the SERVER.
     *
     * The whole filtered set is rows this tab never holds, and paging through them is the server's
     * job — so the browser sends the filters it is showing and hands the bytes straight to the
     * viewer. The file's name is the server's too, because the server is what knows when the data
     * was read.
     */
    async exportCredentials(input) {
      const { api } = correlated();
      try {
        const file = await exportCredentialsCsv(
          api,
          {
            status: input.filter.status,
            templateId: input.filter.templateId,
            q: input.filter.q,
            /*
             * The rest of the screen's filters. This list used to stop at `q` — the fourth place
             * the same three fields were re-typed — so a screen standing in a group still asked
             * for the whole register. `noEmail` travels as the string the query builder takes and
             * the endpoint's schema coerces, exactly as `includeRecipientEmail` does below.
             */
            batchId: input.filter.batchId,
            groupId: input.filter.groupId,
            delivery: input.filter.delivery,
            ...(input.filter.noEmail === true ? { noEmail: 'true' } : {}),
          },
          { includeRecipientEmail: input.includeRecipientEmail === true },
        );
        return { ok: true, filename: file.filename, body: file.blob };
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * The Sheets mirror.
     *
     * Three thin calls: the backend owns every rule — which role may configure it, whether a pasted
     * address is a file id, whether this deployment has a Google credential at all — and this
     * reports what it answered rather than deciding anything first.
     */
    /*
     * An interaction, stored where it survives the refresh.
     *
     * The administrative console does NOT call this, and that is deliberate: a row written from
     * here would be recorded as anonymous, and we know perfectly well who it was. The surfaces that
     * do call it are the public page and the claim page, where the act really does belong to an
     * unidentified visitor.
     */
    async recordInteraction(input) {
      const { api } = correlated();
      try {
        await recordPublicInteraction(api, input.code, input.kind, input.key);
        return { ok: true };
      } catch (error) {
        /* The download, the print window and the navigation already happened. */
        return failure(error);
      }
    },

    async readCredentialHistory(input) {
      const { api } = correlated();
      const c = await idOf(input.code);
      if (!c) return { ok: false, message: 'Kayıt bulunamadı' };
      try {
        return {
          ok: true,
          history: await readCredentialHistory(api, c.id, {
            ...(input.category ? { category: input.category } : {}),
            ...(input.limit !== undefined ? { limit: input.limit } : {}),
            ...(input.offset !== undefined ? { offset: input.offset } : {}),
          }),
        };
      } catch (e) {
        const f = failure(e);
        return { ok: false, ...(f.message ? { message: f.message } : {}) };
      }
    },

    async exportCredentialHistory(input) {
      const { api } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      try {
        const file = await exportCredentialHistoryCsv(api, c.id, input.category);
        return { ok: true, filename: file.filename, body: file.blob };
      } catch (e) {
        return failure(e);
      }
    },

    async readSheetSync() {
      const { api } = correlated();
      try {
        return { ok: true, status: await readSheetSync(api) };
      } catch (e) {
        const f = failure(e);
        return { ok: false, ...(f.message ? { message: f.message } : {}) };
      }
    },

    async saveSheetSync(input) {
      const { api, cid } = correlated();
      try {
        const status = await saveSheetSync(api, input);
        return { ok: true, status, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },

    async runSheetSync() {
      const { api, cid } = correlated();
      try {
        const r = await runSheetSync(api);
        return { ok: true, status: r.status, run: r.outcome, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },

    async resendClaimEmail(input) {
      const { api, cid } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      try {
        const r = await resendClaimEmail(api, c.id, { reason: input.reason }, input.idempotencyKey);
        return await after(r, { publicCode: input.code, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * THE FIRST E-MAIL, SENT LATER.
     *
     * `previewFirstSend` is in the read-only list in `invalidate-reads.ts`: it changes nothing, and
     * bumping the revision after it would make the dialog that asked for it re-read and ask again.
     * `readFirstSendRequest` is there for the same reason — the trap `hydrateCredential` is in that
     * list for. `requestFirstSend` is not: it accepts a send, and every list that shows a delivery
     * state has just become stale.
     */
    async previewFirstSend(input) {
      const { api, cid } = correlated();
      try {
        const preview = await previewFirstSend(api, input.scope, input.kind);
        return { ok: true, preview, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },
    /*
     * ACCEPTANCE LINKS IN BULK. `previewBulkClaimLinks` is in the read-only list in
     * `invalidate-reads.ts` for the trap `previewFirstSend` is there for: it is a question, and
     * bumping the revision after it would make the window that asked re-read and ask again, for
     * ever. `createBulkClaimLinks` is deliberately NOT there — it rotates tokens, so every list
     * showing a record's claim state has just become stale.
     */
    async previewBulkClaimLinks(input) {
      const { api, cid } = correlated();
      try {
        const preview = await previewBulkClaimLinksApi(api, input.scope);
        return { ok: true, preview, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },
    async createBulkClaimLinks(input) {
      const { api, cid } = correlated();
      try {
        const result = await createBulkClaimLinksApi(
          api,
          input.scope,
          input.expectDigest,
          input.confirmRevocations,
        );
        const done = await after(result, { correlationId: cid });
        return { ...done, result };
      } catch (e) {
        return failure(e);
      }
    },
    async requestFirstSend(input) {
      const { api, cid } = correlated();
      try {
        const r = await requestFirstSend(api, input.scope, input.reason, input.idempotencyKey, input.kind);
        /*
         * The accepted request, not a result. The work happens in the outbox worker, so what comes
         * back is an id to follow — and `after` refreshes the lists, because a record that is about
         * to be e-mailed is a record whose delivery state on screen is already out of date.
         */
        const done = await after(r, { correlationId: cid });
        return { ...done, requestId: r.requestId, created: r.created, preview: r.preview };
      } catch (e) {
        return failure(e);
      }
    },
    async listFirstSendRequests(input) {
      const { api, cid } = correlated();
      try {
        const r = await listFirstSendRequests(api, {
          ...(input.credentialId === undefined ? {} : { credentialId: input.credentialId }),
          ...(input.kind === undefined ? {} : { kind: input.kind }),
          ...(input.limit === undefined ? {} : { limit: input.limit }),
        });
        return { ok: true, requests: r.requests, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },
    async readFirstSendRequest(input) {
      const { api, cid } = correlated();
      try {
        const view = await readFirstSendRequest(api, input.requestId);
        return { ok: true, view, correlationId: cid };
      } catch (e) {
        return failure(e);
      }
    },

    async correctCredential(input) {
      const { api, cid } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      /*
       * The revision the FORM was filled from, carried in by the screen — never re-read here.
       *
       * Deriving it from the store at send time defeated the lock it is: between opening the dialog
       * and pressing save, somebody else's correction can move the record on, and re-reading would
       * then quietly aim this form at THEIR revision. The backend's 409 could not fire, and their
       * change was overwritten by a form that had never seen it.
       */
      const baseRevisionId = input.baseRevisionId;
      if (!baseRevisionId) return no('Düzeltme için mevcut revision okunamadı');
      try {
        /*
         * `baseRevisionId` is the optimistic lock: a correction written against a revision that is
         * no longer current comes back 409 rather than overwriting someone else's (PO 7.3).
         */
        /*
         * `changes` is FLAT: claim keys and the correctable date keys side by side, which is what
         * the domain's plan iterates. Nesting them under `claims` made every key an unknown one
         * and the correction came back 422 while the screen reported success.
         */
        const r = await correctCredential(api, c.id, {
          baseRevisionId,
          changes: {
            ...input.claims,
            issueDate: input.issueDate,
            ...(input.expiresAt === null ? {} : { expiresAt: input.expiresAt }),
          },
          reasonCode: correctionReasonCode(input),
          reason: input.reason,
        });
        return await after(r, { publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async adminPublishCredential(input) {
      const { api, cid } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      try {
        const r = await adminPublishCredential(api, c.id, { reason: input.reason });
        return await after(r, { publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async updateRecipientEmail(input) {
      const { api, cid } = correlated();
      const c = await idOf(input.code);
      if (!c) return no('Kayıt bulunamadı');
      try {
        await correctRecipientEmail(api, c.id, { email: input.email, reason: input.reason });
        if (input.resend)
          await resendClaimEmail(
            api,
            c.id,
            { reason: 'E-posta adresi düzeltildi; bağlantı yeniden gönderildi' },
            /* One correction, one send: the correlation id of THIS act is its natural name. */
            `fix-email-${cid}`,
          );
        return await after(null, { publicCode: input.code, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    // ------------------------------------------------------ the recipient's own surface
    async acceptClaim(input) {
      const { api, cid } = correlated();
      try {
        /*
         * The raw token is the URL's, passed straight through to the anonymous route and never
         * stored, logged or put into any state that outlives this call.
         */
        const r = await acceptClaim(api, input.token);
        return ok({ publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async declineClaim(input) {
      const { api, cid } = correlated();
      try {
        const r = await declineClaim(api, input.token);
        return ok({ publicCode: r.credential.publicCode, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * G14 — the recipient's «something is wrong» report. The prototype writes it into the ledger;
     * the backend's `credential_event_type` is a closed enum with no value for it, and adding one
     * is a Product Owner decision (matrix G14). Rather than drop the report into a tab that
     * forgets it, the command says what happened to it.
     */
    async reportClaimIssue() {
      return no(
        'Hata bildirimi için kalıcı bir denetim olayı henüz tanımlı değil (G14, Product Owner kararı bekliyor).',
      );
    },

    async recordLinkedInStep(input) {
      /* The helper's steps share ONE id across the run, so the sequence reads as one act. */
      const correlationId = input.correlationId ?? newCorrelationId();
      const api = deps.api.withCorrelation(correlationId);
      /*
       * Rules 14 and 15: nothing here writes to LinkedIn. `linkedin/sync` records that the
       * recipient asked for it; the three helper-opened steps are telemetry in the reference —
       * session-scoped, no legal value — and are deliberately not persisted here either.
       */
      if (input.step !== 'api_sync_requested') return ok({ correlationId });
      try {
        await createLinkedInSync(api, input.credentialId, null);
        return await after(null, { correlationId });
      } catch (e) {
        return failure(e);
      }
    },

    async recordEngagement(input) {
      const { api, cid } = correlated();
      try {
        await recordPublicEngagement(api, input.code, input.kind);
        return ok({ correlationId: cid });
      } catch (e) {
        /* Swallowed on purpose: this is a measurement, and the member's link already opened. */
        return failure(e);
      }
    },

    // ------------------------------------------------------------------ templates
    /*
     * The wizard composes a whole template record — identity, metadata, permissions, fields and the
     * drawing — in one go. The backend takes it in two: `template.create` opens the record with a
     * default draft, and `template.update_draft` writes the draft the wizard actually composed.
     * Both are needed; a create alone would leave a template whose drawing is not the one the user
     * just designed.
     */
    async createTemplate(input) {
      const { api, cid } = correlated();
      const t = input.template;
      /* Still fail-closed — a template without an issuer is not a thing that can exist — but the
         wizard no longer reaches here on an installation with no profile, and the sentence now
         says what to do rather than only that a read failed (PO 7.11 §5). */
      const { issuerProfileId, issuerProfileAccess } = deps.read().settings;
      if (!issuerProfileId)
        return no(
          issuerProfileAccess === 'forbidden'
            ? 'Kurum profilini görme yetkiniz yok; şablon oluşturmak için sahip hesabı gerekir.'
            : 'Kurum profili yok — «E-posta ve Doğrulama → Kurum kimliği» ekranından oluşturun.',
        );
      let created: Awaited<ReturnType<typeof createTemplate>>;
      try {
        created = await createTemplate(api, {
          issuerProfileId,
          code: t.code,
          name: t.name,
          credentialType: t.draft.subjectType,
          canonicalTitle: t.draft.localizedMetadata.tr?.title ?? t.name,
          publicationPolicy: t.draft.publicationPolicy as never,
        });
      } catch (e) {
        return failure(e);
      }
      /*
       * The template EXISTS from here on, and saying otherwise is the one answer that must not be
       * given.
       *
       * Creating a template and saving its design are two requests, and the second one used to be
       * inside the same `try`: when it failed the wizard reported «Şablon oluşturulamadı» and stayed
       * where it was — while a template with none of the seven steps' work sat in the register,
       * invisible to the person who had just been told nothing happened. So a failed draft write is
       * reported as what it is, the id still travels, and the caller opens the editor on it: the
       * design is recoverable there, which it is not from a screen that denies the template exists.
       */
      try {
        await updateDraft(
          api,
          created.template.id,
          draftToApi(t.draft, { canonicalTitle: t.draft.localizedMetadata.tr?.title ?? t.name }),
        );
      } catch (e) {
        const why = failure(e).message ?? 'Sunucuya ulaşılamadı';
        return {
          ...(await after(created, { correlationId: cid })),
          ok: false,
          partial: 'template_created_without_draft',
          templateId: created.template.id,
          message: `${t.name} oluşturuldu, ancak tasarımı kaydedilemedi: ${why} — editörde tekrar kaydedin.`,
        };
      }
      return await after(created, {
        message: created.template.id,
        templateId: created.template.id,
        correlationId: cid,
      });
    },

    async duplicateTemplate(input) {
      const { api, cid } = correlated();
      try {
        const r = await cloneTemplate(api, input.templateId, {
          code: input.copy.code,
          name: input.copy.name,
        });
        /*
         * The SERVER's id for the copy, not the one the screen minted locally. Navigating to a
         * locally-minted id opened an editor for a template the backend had never heard of.
         */
        return await after(r, { message: r.template.id, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async renameTemplate(input) {
      const { api, cid } = correlated();
      try {
        const r = await renameTemplate(api, input.templateId, input.name, input.expectedName);
        /* The name the SERVER stored, so the screen shows what was written rather than what was typed. */
        return await after(r, { message: r.template.name, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async createCredentialGroup(input) {
      const { api, cid } = correlated();
      try {
        const r = await createCredentialGroup(api, {
          name: input.name,
          ...(input.note === undefined ? {} : { note: input.note }),
        });
        /* The name the SERVER stored, so the screen reports what was written. */
        return await after(r, { message: r.group.name, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async updateCredentialGroup(input) {
      const { api, cid } = correlated();
      try {
        const r = await updateCredentialGroup(api, input.groupId, {
          name: input.name,
          ...(input.note === undefined ? {} : { note: input.note }),
        });
        return await after(r, { message: r.group.name, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async deleteCredentialGroup(input) {
      const { api, cid } = correlated();
      try {
        const r = await deleteCredentialGroup(api, input.groupId);
        return await after(r, {
          message: `«${r.name}» grubu kaldırıldı · ${String(r.releasedMembers)} kayıt gruptan çıktı (kayıtlar silinmedi)`,
          correlationId: cid,
        });
      } catch (e) {
        return failure(e);
      }
    },

    async addCredentialsToGroup(input) {
      const { api, cid } = correlated();
      try {
        const change = await addCredentialsToGroup(api, input.groupId, input.scope);
        const done = await after(change, {
          message:
            change.unchanged > 0
              ? `${String(change.changed)} kayıt «${change.group.name}» grubuna eklendi · ${String(change.unchanged)} kayıt zaten gruptaydı`
              : `${String(change.changed)} kayıt «${change.group.name}» grubuna eklendi`,
          correlationId: cid,
        });
        return { ...done, change };
      } catch (e) {
        return failure(e);
      }
    },

    async removeCredentialsFromGroup(input) {
      const { api, cid } = correlated();
      try {
        const change = await removeCredentialsFromGroup(api, input.groupId, input.scope);
        const done = await after(change, {
          message:
            change.unchanged > 0
              ? `${String(change.changed)} kayıt «${change.group.name}» grubundan çıkarıldı · ${String(change.unchanged)} kayıt grupta değildi`
              : `${String(change.changed)} kayıt «${change.group.name}» grubundan çıkarıldı`,
          correlationId: cid,
        });
        return { ...done, change };
      } catch (e) {
        return failure(e);
      }
    },

    async renameIssuanceBatch(input) {
      const { api, cid } = correlated();
      try {
        const r = await renameIssuanceBatch(api, input.batchId, input.name);
        return await after(r, {
          message: r.batch.name ?? r.batch.sourceFilename ?? 'Adsız yükleme',
          correlationId: cid,
        });
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * `template.archive` exists; there is no unarchive route, and inventing one would be inventing
     * behaviour no canonical contract defines. The screen is told, rather than shown a control that
     * silently does nothing.
     */
    async setTemplateArchived(input) {
      const { api, cid } = correlated();
      if (!input.archived)
        return no('Arşivden çıkarma komutu backend sözleşmesinde tanımlı değil (E6 ile aynı karar kümesi).');
      try {
        const r = await archiveTemplate(api, input.templateId, 'Şablon arşivlendi');
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async saveDraft(input) {
      const { api, cid } = correlated();
      const t = deps.read().templates.find((x) => x.id === input.templateId);
      try {
        /*
         * The layout the backend already holds is passed back in, so the keys this projection does
         * not model survive the round trip instead of being dropped by a screen that never read them.
         */
        const r = await updateDraft(
          api,
          input.templateId,
          draftToApi(input.draft, {
            canonicalTitle: input.canonicalTitle,
            existingLayout: (t?.layout as Record<string, unknown>) ?? {},
          }),
        );
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async publishRelease(input) {
      const { api, cid } = correlated();
      try {
        const r = await publishRelease(api, input.templateId);
        /*
         * The SERVER's release, handed back rather than left behind.
         *
         * `after` re-reads the world, so the store already holds this release — and the gate used
         * to write its own locally built one over the top of it, under an id shaped
         * `<templateId>-r<n>`. The next single issuance then sent that where a uuid was required
         * and came back «Şablon sürümü — biçimi geçersiz»; a page reload «fixed» it because the
         * reload threw the invented release away. The identity travels with the answer now.
         */
        return await after(r, {
          correlationId: cid,
          release: {
            id: r.release.id,
            releaseNumber: r.release.releaseNumber,
            releaseHash: r.release.releaseHash,
          },
        });
      } catch (e) {
        /*
         * The gate's refusal names its rows, and the administrator has to be able to read them.
         *
         * `publish_blocked` carries every blocking problem in the error's `details`, one per rule,
         * and only the bare sentence («release checklist has blocking problems») was being shown —
         * a refusal with nothing to act on. The rules are the reason the gate exists.
         */
        const refused = failure(e);
        if (isApiError(e) && e.details.length > 0) {
          const rules = e.details.map((d) => d.message).join(' · ');
          return { ...refused, message: `${refused.message ?? 'Yayınlanamadı'} — ${rules}` };
        }
        return refused;
      }
    },

    async deleteDraft(input) {
      const { api, cid } = correlated();
      try {
        const r = await deleteDraft(api, input.templateId);
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    // ------------------------------------------------------------------ assets
    /*
     * The file's bytes, to the server, and the server's answer back.
     *
     * Nothing is validated here first. The backend sniffs the content, sanitises an SVG with its own
     * allowlist, strips a raster's metadata chunks, refuses a format it does not recognise and scans the
     * bytes fail-closed — and it takes the digest AFTER all of that. So a browser-side pre-check could
     * only ever disagree with it: refuse something the server would have accepted (an SVG it can
     * sanitise) or compute a digest the server does not have. The id and the digest that come back are
     * the stored ones, and they are what a template reference may carry.
     *
     * The bytes are then read back through `asset.read` so the picture can be drawn. A failure there is
     * not a failed upload — the asset is stored — so the record is returned without a preview rather
     * than reported as a refusal.
     */
    async uploadAsset(input) {
      const { api } = correlated();
      try {
        const bytes = await input.file.arrayBuffer();
        const r = await uploadAssetBytes(api, bytes, {
          contentType: input.file.type || 'application/octet-stream',
          ...(input.file.name ? { filename: input.file.name } : {}),
        });
        let contentUrl = '';
        try {
          contentUrl = (await readAssetContent(api, r.asset.id)).objectUrl;
        } catch {
          /* Stored, not readable by this session: the library lists it without a picture. */
        }
        const asset = libraryAssetFromApi(r.asset, contentUrl);
        if (contentUrl) registerRenderLibrary({ assets: [{ ...asset }] });
        return { ok: true, asset, deduped: r.report.deduped === true };
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * Same shape for a face, plus the metadata it is stored under. The licence acknowledgement travels
     * with the request because the server refuses a face without it — the checkbox is a server-side
     * gate, not a local formality — and the face is registered with the browser from the bytes that
     * came back, so «the uploaded face» on screen is the face the server has.
     */
    async uploadFont(input) {
      const { api } = correlated();
      try {
        const bytes = await input.file.arrayBuffer();
        const r = await uploadFontBytes(api, bytes, {
          contentType: input.file.type || 'application/octet-stream',
          family: input.meta.family,
          weight: input.meta.weight,
          style: input.meta.style === 'italic' ? 'italic' : 'normal',
          licenseAcknowledged: input.meta.licenseAcknowledged,
          ...(input.file.name ? { filename: input.file.name } : {}),
        });
        let state: 'loaded' | 'failed' | 'pending' = 'pending';
        try {
          const content = await readFontContent(api, r.font.id);
          state = await loadFontFace(r.font.family, content.bytes, {
            weight: r.font.weight,
            style: r.font.style,
          });
        } catch {
          /* Stored, but this session cannot load the face: said as `pending`, never as `loaded`. */
        }
        const font = libraryFontFromApi(r.font, state);
        registerRenderLibrary({
          fonts: [
            { id: font.id, sha256: font.sha256, family: font.family, weight: font.weight, style: font.style },
          ],
        });
        return { ok: true, font, deduped: r.deduped === true };
      } catch (e) {
        return failure(e);
      }
    },

    async applyAssetToTemplate(input) {
      const { api, cid } = correlated();
      const t = deps.read().templates.find((x) => x.id === input.templateId);
      if (!t) return no('Şablon bulunamadı');
      try {
        /* The layer edit itself is the draft save: one write, one audit entry, one release gate. The
           draft saved is the one the caller composed, not the store's — the store has not been told. */
        const r = await updateDraft(
          api,
          input.templateId,
          draftToApi(input.draft, {
            canonicalTitle: t.name,
            existingLayout: (t.layout as Record<string, unknown>) ?? {},
          }),
        );
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    // ------------------------------------------------------------------ batches
    /*
     * The real sequence, in the backend's own order. The records the screen composed are NOT sent:
     * the server re-reads the file, re-validates every row against the release's field definitions
     * and mints its own public codes, which is the only reading of the list that can be trusted
     * (rule 11, fail-closed; rule 6, the code is the system's).
     */
    async createBatch(input) {
      const { api, cid } = correlated();
      if (!input.file.bytes)
        return no('Bu liste tarayıcıda üretildi; gerçek üretim için bir dosya yükleyin.');
      /*
       * TWO ACTS, AND THEN WATCHING.
       *
       * This used to be three calls — create, validate, execute — each of which had to contain all
       * of its own work, and then it became a LOOP of slices here. Slices fixed the thirty-second
       * timeout; they did not fix the browser going away, because the next slice still only existed
       * because this code asked for it. Close the tab and the batch stopped half done.
       *
       * So now: upload, then ASK THE SERVER TO FINISH IT. The acceptance is one call that persists
       * the plan and the request together, and from the instant it answers the run belongs to the
       * server's own worker. Everything after it here is reading — the operator watches while the
       * page is open, and closing it changes nothing about the work.
       */
      const attemptKey = await attemptKeyForFile(input.file.bytes, input.release.id);
      try {
        input.onProgress?.({ phase: 'creating', done: 0, total: 0 });
        const batchId = await openBatch(api, attemptKey, {
          bytes: input.file.bytes,
          name: input.file.name,
          releaseId: input.release.id,
        });
        input.onProgress?.({ phase: 'accepting', done: 0, total: 0 });
        const started = await startServerRun(
          api,
          batchId,
          {
            columnMapping: batchMappingToApi(input.columnMapping),
            options: {
              lang: input.options.lang,
              sendEmail: input.options.sendEmail,
              duplicateDecision: input.options.duplicateDecision,
              defaultIssueDate: input.options.defaultIssueDate,
              defaultExpiresAt: input.options.defaultExpiresAt,
            },
          },
          attemptKey,
        );
        if (started.kind === 'refused')
          return {
            ok: false,
            message: started.message || 'Toplu üretim kabul edilmedi.',
            outcome: 'refused',
            ...(started.code ? { code: started.code } : {}),
            correlationId: cid,
          };
        /*
         * THE JOB IS THE SERVER'S FROM HERE. The screen is told now, before any watching, because
         * this is the moment the page became safe to close — and it is told whether a worker will
         * actually carry it, so it cannot promise that on a deployment where nothing will.
         */
        input.onAccepted?.({
          batchId: started.batchId,
          processing: started.processing,
          alreadyAccepted: started.alreadyAccepted,
        });

        const seen = await followBatch(
          api,
          started.batchId,
          { ...(input.onProgress ? { onProgress: input.onProgress } : {}) },
          { ...(input.signal ? { signal: input.signal } : {}) },
        );
        if (seen.kind === 'refused')
          return {
            ok: false,
            message: seen.message || 'Yüklemenin durumu okunamadı.',
            outcome: 'refused',
            ...(seen.code ? { code: seen.code } : {}),
            correlationId: cid,
          };
        if (seen.kind === 'unobserved') {
          /*
           * THIS TAB STOPPED WATCHING — the run did not stop. Reported as unresolved and the screen
           * is sent to the batch, where the server's own counts are printed. Calling this
           * «tamamlanamadı» is what makes somebody upload the list a second time.
           */
          await deps.refresh();
          return {
            ok: true,
            message: seen.batchId,
            /*
             * Two codes, because they are two states and only one of them is progress. A tab that
             * stopped reading a run a worker is carrying is «go and look at the batch»; an accepted
             * run on a deployment with no worker is waiting for a person, and saying it is running
             * would promise something nothing is doing.
             */
            code: seen.processing ? 'batch_running_on_server' : 'batch_accepted_not_processing',
            outcome: 'unresolved',
            correlationId: cid,
          };
        }
        if (seen.kind === 'stopped') {
          /* The server stopped and said why. A real answer, and not a success. */
          await deps.refresh();
          return {
            ok: false,
            message: seen.batchId,
            code: seen.lastError ?? 'batch_run_stopped',
            outcome: 'refused',
            correlationId: cid,
          };
        }
        forgetBatchId(attemptKey);
        return await after(null, { message: seen.batchId, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * F-row gap: there is no «re-queue the bounced deliveries of a batch» command. Resending is
     * per credential and needs a reason, which is the audit trail the batch-wide button would
     * skip. The screen says so rather than appearing to have done it.
     */
    async requeueBouncedEmails() {
      return no('Toplu yeniden gönderim komutu yok; her kayıt kendi gerekçesiyle yeniden gönderilir.');
    },

    // ------------------------------------------------------------------ settings
    /**
     * Save the issuer identity — which on an installation that has none is CREATING it.
     *
     * The settings tab is one form and stays one form (PO 7.11 §5). What differs underneath is
     * which command it is: with a profile this is `issuer.update`, without one it is
     * `issuer.create`, an owner-only command that the backend refuses the moment any profile
     * exists. A deployment whose issuer read was REFUSED is not a deployment without a profile,
     * and it must not be offered a creation it cannot perform — hence the access check rather than
     * an empty id.
     *
     * This replaces a flat «Kurum profili okunamadı», which was the whole of the lock: no profile
     * meant no template, no template meant no credential, and no screen could make the first one.
     */
    async updateIssuerProfile(input) {
      const { api, cid } = correlated();
      const settings = deps.read().settings;
      if (settings.issuerProfileAccess === 'forbidden')
        return no('Kurum profilini görme yetkiniz yok; bu işlem için sahip hesabı gerekir.');

      const id = settings.issuerProfileId;
      if (id) {
        try {
          const r = await updateIssuerProfile(api, id, input);
          return await after(r, { correlationId: cid });
        } catch (e) {
          return failure(e);
        }
      }

      /*
       * The first profile. The three fields the backend requires are checked here too — not
       * instead of the schema, which is still the gate that decides, but so the refusal names the
       * field in the product's own words rather than costing a round trip to say «request shape is
       * invalid». Nothing is defaulted: an issuer's name and address are facts about a real
       * organisation, and a value nobody typed would end up on a public credential page.
       */
      const text = (key: string): string => {
        const v = input[key];
        return typeof v === 'string' ? v.trim() : '';
      };
      const missing = (
        [
          ['issuerDisplayName', 'Veren kuruluş'],
          ['linkedinIssuerName', 'LinkedIn kurum adı'],
          ['publicBase', 'Public adres'],
        ] as const
      )
        .filter(([key]) => text(key) === '')
        .map(([, label]) => label);
      if (missing.length > 0)
        return no(`Kurum profili oluşturulamadı — zorunlu alan boş: ${missing.join(', ')}`);

      try {
        const r = await createIssuerProfile(api, {
          ...input,
          issuerDisplayName: text('issuerDisplayName'),
          linkedinIssuerName: text('linkedinIssuerName'),
          publicBase: text('publicBase'),
        } as Parameters<typeof createIssuerProfile>[1]);
        /* `after` re-reads from the server, so the REAL id enters app state from the backend and
           is never derived here — a reload then shows what was actually stored. */
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async saveEmailTemplate(input) {
      const { api, cid } = correlated();
      try {
        const r = await updateEmailTemplate(api, input.key, {
          lang: input.lang as 'tr' | 'en',
          subject: input.subject,
          body: input.bodyHtml,
          reason: 'E-posta şablonu güncellendi',
        });
        return await after(r, { correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async sendEmailTemplateTest(input) {
      const { api, cid } = correlated();
      try {
        /* The recipient is the signed-in user's own verified address; the body carries no `to`. */
        const r = await sendEmailTemplateTest(api, input.key, input.lang as 'tr' | 'en');
        return ok({ message: r.to, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    async hydrateCredential(input) {
      const { api } = correlated();
      const base = await idOf(input.code);
      if (!base) return no('Kayıt bulunamadı');
      try {
        const { credential, deliveries } = await hydrateCredentialFromApiReads(api, base, base.id);
        deps.patchLocal((db) => {
          const i = db.credentials.findIndex((c) => c.id === base.id);
          /* Insert, not only replace: the register is a page now, so the record the reader opened is
             often one the store has never seen. */
          if (i >= 0) db.credentials[i] = credential;
          else db.credentials.push(credential);
          /* The detail screen's delivery tab reads the global list, keyed by credential. */
          db.deliveries = [...db.deliveries.filter((d) => d.credentialId !== base.id), ...deliveries];
        });
        return ok({ publicCode: input.code });
      } catch (e) {
        return failure(e);
      }
    },

    async listUsers() {
      const { api } = correlated();
      try {
        const r = await listUsers(api);
        return { ok: true, users: r.users as AdministrativeUserView[] };
      } catch (e) {
        const f = failure(e);
        return { ok: false, users: [], ...(f.message ? { message: f.message } : {}) };
      }
    },

    /*
     * `user.create`. Three fields go out; the provider account is resolved on the server.
     *
     * The row that comes back is the server's own sanitized projection, so the screen shows what the
     * register will show on its next read rather than what the form hoped for.
     */
    async createUser(input) {
      const { api, cid } = correlated();
      try {
        const r = await createUser(api, {
          email: input.email,
          displayName: input.displayName,
          role: input.role,
        });
        return {
          ...ok({ correlationId: cid }),
          user: r.user as AdministrativeUserView,
          providerAccount: r.providerAccount,
        };
      } catch (e) {
        return failure(e);
      }
    },

    async activateUser(input) {
      const { api, cid } = correlated();
      try {
        const r = await activateUser(api, input.userId, input.reason);
        return ok({ message: r.user.status, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /*
     * The success sentence is the SERVER's, not the click's. What comes back is the assignment the
     * backend actually opened, so the message carries `assigned.role` — if the server granted
     * something other than what was asked for, that is what the screen would say.
     */
    async assignRole(input) {
      const { api, cid } = correlated();
      try {
        const r = await assignRole(api, input.userId, { role: input.role, reason: input.reason });
        return ok({ message: r.assigned.role, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    /* Likewise: the status the server reports, which is `suspended` — and its roles are gone too. */
    async disableUser(input) {
      const { api, cid } = correlated();
      try {
        const r = await disableUser(api, input.userId, input.reason);
        return ok({ message: r.user.status, correlationId: cid });
      } catch (e) {
        return failure(e);
      }
    },

    patchLocal(mutate) {
      deps.patchLocal(mutate);
    },

    refresh: deps.refresh,
  };
}
